// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  MembershipReportData,
  MembershipRulesDefinition,
  MembershipScopeOptions,
} from '@/membership/api/membership-types';
import { MembersList } from '@/membership/components/members-list';
import { MembershipReportPreview } from '@/membership/components/membership-report-preview';
import { MembershipRulesSection } from '@/membership/components/membership-rules-section';
import {
  useRunMembershipRules,
  useSaveMembershipRules,
} from '@/membership/hooks/use-membership';
import { useMembershipView } from '@/membership/hooks/use-membership-view';
import {
  toDefinitionWithScope,
  toScope,
} from '@/membership/utilities/membership-rules-scope';
import { toBlockedReason } from '@/membership/utilities/membership-rules-validity';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

// The count follows edits rather than waiting for a button. This is the pause
// after a change before it refreshes.
const RUN_DEBOUNCE_MS = 500;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectKey: string;
  scopeOptions: MembershipScopeOptions;
  definition: MembershipRulesDefinition;
  onSaved: () => void;
}

/**
 * Edits the project's membership report. The count beside the rules answers
 * the draft, so the effect of a change is visible before it is saved.
 */
export function MembershipRulesDialog(props: Props) {
  const [definition, setDefinition] = useState<MembershipRulesDefinition>(
    props.definition
  );
  const [report, setReport] = useState<MembershipReportData | null>(null);
  // The rules the shown report answers. It lags runKey from the moment an edit
  // lands until that edit's run comes back.
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [isSerialsOpen, setIsSerialsOpen] = useState(false);

  const runMutation = useRunMembershipRules();
  const saveMutation = useSaveMembershipRules({
    onSuccess: () => {
      props.onSaved();
      props.onClose();
    },
  });

  // Each failure lives in its own mutation, so it describes the newest call and
  // nothing else: an edit or a retry clears it, a success clears it, and a slow
  // run that fails after a newer one started never reaches the screen.
  const runFailure = runMutation.error;
  const saveFailure = saveMutation.error;
  const runErrorMessage = runFailure?.userMessage ?? null;
  const saveErrorMessage = saveFailure?.userMessage ?? null;

  const view = useMembershipView(report);
  const scope = toScope(definition);
  const hasItems = scope.items.isAll || scope.items.keys.length > 0;
  const hasInclusion = scope.joinRules.length > 0;
  const isScopeComplete = hasItems && hasInclusion;

  // Which rules the preview is answering. A run is tagged with this so a slow
  // answer arriving after a newer edit can be dropped, and comparing the text
  // rather than the object means an edit that changes nothing runs nothing.
  // Memoised because the scrub bar's state lives in this component, so every
  // drag tick re-renders it and would otherwise re-serialize the rules.
  const runKey = useMemo(() => JSON.stringify(definition), [definition]);
  const runRules = runMutation.mutate;
  const resetRun = runMutation.reset;
  const resetSave = saveMutation.reset;
  const isReportLoading = settledKey !== runKey;

  const blockedReason = toBlockedReason({
    hasItems,
    hasInclusion,
    isCountLoading: isReportLoading,
  });

  const latestKeyRef = useRef(runKey);
  latestKeyRef.current = runKey;

  // The dialog is mounted for the life of the tab so it can animate out, which
  // means its draft would otherwise outlive the edit that made it. Closing
  // without saving has to discard, and rules saved elsewhere have to be picked
  // up, so the draft is taken from the props each time it opens.
  const wasOpenRef = useRef(false);
  const savedDefinition = props.definition;
  useEffect(() => {
    if (props.isOpen && !wasOpenRef.current) {
      setDefinition(savedDefinition);
      setReport(null);
      setSettledKey(null);
      resetRun();
      resetSave();
      setIsSerialsOpen(false);
    }
    wasOpenRef.current = props.isOpen;
  }, [props.isOpen, savedDefinition, resetRun, resetSave]);

  useEffect(() => {
    // The dialog stays mounted so it can animate out and hand focus back, so
    // the preview only runs while it is actually open.
    if (!props.isOpen || !isScopeComplete) return undefined;
    // The edit that starts this run also retires the last one's failure, so the
    // error the user is fixing does not outlive the fix.
    resetRun();
    const timer = setTimeout(() => {
      runRules(
        { projectKey: props.projectKey, definition },
        {
          // A run describes the definition it was sent with. An edit during a
          // slow run starts a newer one, and the older answer landing last
          // would paint a preview for rules the user has already moved off.
          onSuccess: (report) => {
            if (latestKeyRef.current === runKey) setReport(report);
          },
          // Settled rather than success: a failed run has to stop the spinner
          // too, or the panel waits on a report that is never coming.
          onSettled: () => {
            if (latestKeyRef.current === runKey) setSettledKey(runKey);
          },
        }
      );
    }, RUN_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [
    runKey,
    definition,
    props.isOpen,
    props.projectKey,
    runRules,
    resetRun,
    isScopeComplete,
  ]);

  // A rejection the user can act on is shown beside the rules. Anything else is
  // a support problem, and putting it in the form suggests an edit would fix it.
  // Which action failed decides the wording, because a failed save reported as
  // a failed read sends the user looking in the wrong place.
  const supportErrorMessage = toSupportErrorMessage({
    hasRunFailed: runFailure !== null && runErrorMessage === null,
    hasSaveFailed: saveFailure !== null && saveErrorMessage === null,
  });

  // A save in flight cannot be recalled, so the ways out are closed until it
  // answers. Leaving them open lets a user dismiss the dialog and still have
  // the draft written underneath them.
  const isSaving = saveMutation.isPending;

  return (
    <Dialog
      open={props.isOpen}
      onClose={isSaving ? undefined : props.onClose}
      maxWidth={isSerialsOpen ? 'lg' : 'md'}
      fullWidth
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'row',
          height: '90vh',
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: 0,
        }}
      >
        <SupportSnackbar
          isOpen={supportErrorMessage !== null}
          message={supportErrorMessage ?? ''}
          onClose={() => {
            runMutation.reset();
            saveMutation.reset();
          }}
        />
        <DialogTitle sx={{ pb: 1 }}>
          Edit membership rules
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, textWrap: 'pretty' }}
          >
            Select the rules for when assets join (membership starts) or leave
            (membership ends)
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <MembershipRulesSection
              scope={scope}
              items={props.scopeOptions.items}
              onChange={(next) => {
                setDefinition(toDefinitionWithScope(definition, next));
              }}
              excludeReport={
                <MembershipReportPreview
                  view={view}
                  isSerialsOpen={isSerialsOpen}
                  onToggleSerials={() => {
                    setIsSerialsOpen(!isSerialsOpen);
                  }}
                  title="Preview"
                  caption="Members on the chosen day with these rules"
                  report={report}
                  isLoading={isReportLoading}
                  isScopeComplete={isScopeComplete}
                  errorMessage={runErrorMessage}
                />
              }
            />

            {saveErrorMessage !== null && (
              <Alert severity="error">{saveErrorMessage}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Tooltip arrow title={blockedReason ?? ''}>
            {/* The span keeps the tooltip working while the button is disabled. */}
            <span>
              <LoadingButton
                variant="contained"
                loading={saveMutation.isPending}
                disabled={blockedReason !== null}
                onClick={() => {
                  saveMutation.mutate({
                    projectKey: props.projectKey,
                    definition,
                  });
                }}
              >
                Save rules
              </LoadingButton>
            </span>
          </Tooltip>
        </DialogActions>
      </Box>

      {isSerialsOpen && report !== null && (
        <Box
          sx={{
            width: 300,
            borderLeft: 1,
            borderColor: 'divider',
            p: 2,
            overflow: 'hidden',
            // The list measures the space left for it, so the column it sizes
            // against has to be a real one.
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <MembersList
            members={view.members}
            emptyMessage="No members on this day"
            onClose={() => {
              setIsSerialsOpen(false);
            }}
          />
        </Box>
      )}
    </Dialog>
  );
}

interface SupportErrorInput {
  hasRunFailed: boolean;
  hasSaveFailed: boolean;
}

/** What to say when a failure is nothing the user can act on. */
function toSupportErrorMessage(input: SupportErrorInput): string | null {
  if (input.hasSaveFailed) {
    return 'An error occurred while saving the membership rules.';
  }
  if (input.hasRunFailed) {
    return 'An error occurred while reading the membership report.';
  }
  return null;
}
